import { useEffect, useState } from "react";
import { ChatList } from "../components/ChatList";
import { command } from "../utils/backend";
import type { ChatResponse, Message } from "../types";

interface SessionSummary {
  session_id: string;
  first_at: string;
  last_at: string;
  events: number;
  messages: number;
  model_turns: number;
  tokens: number;
  preview: string | null;
}

function relTime(iso: string): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function ReviewPage({ onBack }: { onBack: () => void }) {
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [error, setError] = useState("");
  const [openSession, setOpenSession] = useState<{
    id: string;
    messages: Message[];
  } | null>(null);

  useEffect(() => {
    fetch("/api/admin/sessions")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<SessionSummary[]>;
      })
      .then(setSessions)
      .catch((e) => setError(String(e)));
  }, []);

  const openTranscript = (id: string) => {
    setError("");
    command<ChatResponse>("get_session", { session_id: id })
      .then((response) => {
        const messages: Message[] = response.messages.map((rm, i) => ({
          id: `${i}`,
          role: rm.role as Message["role"],
          content: rm.content ?? "",
          reasoning: rm.reasoning ?? undefined,
          toolCalls: rm.tool_calls ?? undefined,
          toolCallId: rm.tool_call_id ?? undefined,
          toolName: rm.tool_name ?? undefined,
          timestamp: i,
        }));
        setOpenSession({ id, messages });
      })
      .catch((e) => setError(String(e)));
  };

  if (openSession) {
    return (
      <div className="flex flex-col h-screen bg-[#f7f8fa]">
        <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200/70 px-4 py-2.5 shrink-0">
          <div className="flex items-center gap-3 max-w-3xl mx-auto">
            <button
              onClick={() => setOpenSession(null)}
              className="p-2 text-slate-400 hover:text-[#005bbb] hover:bg-blue-50 rounded-xl transition-colors"
              title="Back to session list"
            >
              <svg
                className="w-[18px] h-[18px]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.7}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                />
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-[15px] font-semibold text-slate-900 leading-tight">
                Session transcript
              </h1>
              <p className="text-[11px] text-slate-400 font-mono truncate">
                {openSession.id}
              </p>
            </div>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto chat-scroll">
          <div className="max-w-3xl mx-auto px-4 py-6">
            <ChatList messages={openSession.messages} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[#f7f8fa]">
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200/70 px-4 py-2.5 shrink-0">
        <div className="flex items-center gap-3 max-w-5xl mx-auto">
          <button
            onClick={onBack}
            className="p-2 text-slate-400 hover:text-[#005bbb] hover:bg-blue-50 rounded-xl transition-colors"
            title="Back to chat"
          >
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.7}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-[15px] font-semibold text-slate-900 leading-tight">
              Chat review
            </h1>
            <p className="text-[11px] text-slate-400 leading-tight">
              All sessions, most recent first — for troubleshooting and
              improvement
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto chat-scroll">
        <div className="max-w-5xl mx-auto px-4 py-6">
          {error && (
            <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-4">
              {error}
            </div>
          )}
          {!sessions && !error && (
            <p className="text-[13px] text-slate-400">Loading sessions…</p>
          )}
          {sessions && sessions.length === 0 && (
            <p className="text-[13px] text-slate-400">
              No sessions recorded yet.
            </p>
          )}
          {sessions && sessions.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="px-4 py-2.5 font-medium">Last activity</th>
                    <th className="px-4 py-2.5 font-medium">First message</th>
                    <th className="px-4 py-2.5 font-medium text-right">Msgs</th>
                    <th className="px-4 py-2.5 font-medium text-right">
                      Turns
                    </th>
                    <th className="px-4 py-2.5 font-medium text-right">
                      Tokens
                    </th>
                    <th className="px-4 py-2.5 font-medium">Session</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr
                      key={s.session_id}
                      onClick={() => openTranscript(s.session_id)}
                      className="border-b border-slate-50 last:border-0 cursor-pointer hover:bg-blue-50/40 transition-colors"
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap text-slate-600">
                        {relTime(s.last_at)}
                      </td>
                      <td className="px-4 py-2.5 text-slate-700 max-w-md">
                        <span className="line-clamp-1">
                          {s.preview ?? (
                            <span className="text-slate-300">—</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-600">
                        {s.messages}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-600">
                        {s.model_turns}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-600">
                        {s.tokens > 0 ? s.tokens.toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400">
                        {s.session_id.slice(0, 8)}…
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
